import React, { useRef, useState } from "react";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputTextarea } from "primereact/inputtextarea";
import { Message } from "primereact/message";
import { Toast } from "primereact/toast";

import { FundingCampaignNote, addFundingCampaignNote } from "../../../services/fundingCampaign";

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });

interface Props {
  submissionId: string;
  notes: FundingCampaignNote[];
  onNoteAdded: () => void;
}

const SubmissionNotes: React.FC<Props> = ({ submissionId, notes, onNoteAdded }) => {
  const toastRef = useRef<Toast>(null);
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    const trimmed = body.trim();
    if (!trimmed) return;
    setSaving(true);
    setError(null);
    try {
      await addFundingCampaignNote(submissionId, { body: trimmed });
      setBody("");
      toastRef.current?.show({ severity: "success", summary: "Note added", life: 3000 });
      onNoteAdded();
    } catch (err: any) {
      setError(err.message ?? "Failed to add note.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Toast ref={toastRef} />
      <Card title="Notes">
        <div className="flex flex-column gap-3">
          <div className="flex flex-column gap-2">
            <InputTextarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={3}
              placeholder="Add a note…"
              maxLength={2000}
              className="w-full"
              autoResize
            />
            {error && <Message severity="error" text={error} className="w-full" />}
            <div className="flex justify-content-end">
              <Button
                label="Add note"
                icon="pi pi-plus"
                onClick={handleSubmit}
                loading={saving}
                disabled={!body.trim()}
              />
            </div>
          </div>

          {notes.length > 0 && (
            <ul className="list-none p-0 m-0 flex flex-column gap-3">
              {[...notes]
                .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
                .map((note) => (
                  <li key={note.id}>
                    <div
                      className="p-3 border-round text-sm text-700"
                      style={{ background: "var(--blue-50)", borderLeft: "3px solid var(--blue-200)" }}
                    >
                      {note.body}
                    </div>
                    <div className="text-xs text-400 mt-1">
                      {note.created_by} · {formatDate(note.created_at)}
                    </div>
                  </li>
                ))}
            </ul>
          )}
        </div>
      </Card>
    </>
  );
};

export default SubmissionNotes;
